import { validateDiscoveryResponse } from '../03-feed-session.mjs';
import { photoSource } from './28-image-sources.mjs';
/** Public discovery presentation. Server DTOs own eligibility, reasons and follow state;
 * preview observations use only the canonical opted-in exposure pipeline, never read marks or enrolment. */
export function createRecommendations(ctx) {
  let generation = 0;
  function dispose() {
    generation++;
  }
  function mount(target, { context = 'search', postId, title = 'More to explore' } = {}) {
    const current = ++generation;
    const actor = ctx.state.me.id;
    const route = ctx.route().join('/');
    const query = ctx.query?.trim() ?? '';
    const format = ['wall', 'visual', 'talk'].includes(ctx.experience) ? ctx.experience : 'wall';
    const learning = ctx.state.settings?.learning;
    const interestEpoch = ctx.state.settings?.interestEpoch;
    const feedGeneration = ctx.feed?.generation;
    const mode = ctx.mode;
    const contentScope = ctx.contentScope ?? 'ordinary';
    const discoveryPolicyVersion = ctx.discoveryPolicyVersion;
    if (
      context === 'following' &&
      (ctx.feedScope !== 'following' ||
        mode === 'latest' ||
        !ctx.feed?.started ||
        ctx.feed.cursor !== null)
    )
      return;
    const section = ctx.e('section', 'recommendations');
    section.setAttribute('aria-label', ctx.uiText(title));
    section.dataset.recommendationContext = context;
    const head = ctx.e('div', 'recommendation-heading');
    head.append(
      ctx.e('h2', '', ctx.uiText(title)),
      ctx.e('span', 'recommendation-label', ctx.uiText('Suggestions')),
    );
    const rail = ctx.e('ul', 'recommendation-rail');
    rail.setAttribute('aria-label', ctx.uiText('Suggested public pages and posts'));
    const status = ctx.e('p', 'recommendation-status', ctx.uiText('Finding something to explore…'));
    status.setAttribute('role', 'status');
    const tools = ctx.e('div', 'recommendation-tools');
    section.append(head, rail, status, tools);
    target.append(section);
    let pending = false,
      nextCursor,
      count = 0;
    const seen = new Set();
    const active = () =>
      current === generation &&
      section.isConnected &&
      ctx.state?.me?.id === actor &&
      ctx.state.settings?.learning === learning &&
      ctx.state.settings?.interestEpoch === interestEpoch &&
      ctx.route().join('/') === route &&
      (['wall', 'visual', 'talk'].includes(ctx.experience) ? ctx.experience : 'wall') === format &&
      ctx.mode === mode &&
      (ctx.contentScope ?? 'ordinary') === contentScope &&
      ctx.discoveryPolicyVersion === discoveryPolicyVersion &&
      (context !== 'search' || (ctx.query?.trim() ?? '') === query) &&
      (context !== 'following' ||
        (ctx.feedScope === 'following' &&
          ctx.mode === mode &&
          (ctx.contentScope ?? 'ordinary') === contentScope &&
          ctx.discoveryPolicyVersion === discoveryPolicyVersion &&
          ctx.feed?.generation === feedGeneration &&
          ctx.feed.cursor === null));
    function picture(post, user) {
      const media = post?.media?.find((item) => item.kind === 'image');
      const src = media?.url ?? post?.image ?? user?.coverImage ?? user?.avatarImage;
      const frame = ctx.e('div', 'recommendation-media');
      if (src) {
        const ratio = Number(media?.ratio ?? post?.imageAspectRatio ?? 1);
        const image = ctx.field('img', {
          src: photoSource(src, 640, media?.variants),
          alt: media?.alt ?? post?.alt ?? '',
          loading: 'lazy',
          decoding: 'async',
        });
        image.style.aspectRatio = String(Number.isFinite(ratio) && ratio > 0 ? ratio : 1);
        frame.append(image);
      } else
        frame.append(
          ctx.e(
            'span',
            'recommendation-placeholder',
            post
              ? (post.text || post.title || ctx.uiText('View post')).slice(0, 180)
              : (user?.name ?? '').slice(0, 1),
          ),
        );
      return frame;
    }
    function card({ post, page, reason, snapshot, exposureFormat }) {
      const user = page?.user;
      const id = post?.id ?? user?.id;
      const key = `${post ? 'post' : 'page'}:${id}`;
      if (!id || seen.has(key) || count >= 24) return;
      seen.add(key);
      count++;
      const item = ctx.e('li', 'recommendation-card');
      item.dataset.recommendationId = key;
      if (post && /^[a-f0-9]{48}$/.test(snapshot ?? '') && exposureFormat === format) {
        item.dataset.recommendationPost = '';
        item.dataset.postId = post.id;
        item.dataset.exposureSnapshot = snapshot;
        item.dataset.exposureFormat = exposureFormat;
      }
      const label =
        post?.title || post?.text?.split(/[.!?\n]/)[0] || user?.name || ctx.uiText('View post');
      const link = ctx.link(
        label,
        post ? `#/post/${id}` : `#/profile/${id}`,
        'recommendation-open',
      );
      link.replaceChildren(picture(post, user), ctx.e('span', 'recommendation-title', label));
      link.setAttribute('aria-label', label);
      const copy = ctx.e('p', 'recommendation-reason', ctx.uiText(reason || 'From public pages'));
      item.append(link, copy);
      if (user && ctx.featureEnabled('following')) {
        let following = page.following === true;
        let changing = false;
        const follow = ctx.button(
          following ? 'Following' : 'Follow',
          async () => {
            if (!active() || changing) return;
            const desired = !following;
            changing = true;
            try {
              await ctx.command(
                { type: 'follow', id: user.id, following: desired },
                { paint: false },
              );
              if (!active()) return;
              following = desired;
              follow.textContent = ctx.uiText(following ? 'Following' : 'Follow');
              follow.setAttribute('aria-pressed', String(following));
              follow.setAttribute(
                'aria-label',
                `${ctx.uiText(following ? 'Unfollow' : 'Follow')} ${user.name}`,
              );
              status.textContent = ctx.uiText(following ? 'Page followed.' : 'Page unfollowed.');
            } catch (error) {
              if (active()) status.textContent = ctx.uiText(error.message);
            } finally {
              changing = false;
            }
          },
          'secondary-button recommendation-follow',
        );
        follow.setAttribute(
          'aria-label',
          `${ctx.uiText(following ? 'Unfollow' : 'Follow')} ${user.name}`,
        );
        follow.setAttribute('aria-pressed', String(following));
        item.append(follow);
      } else
        item.append(
          ctx.link(
            ctx.uiText(post ? 'View post' : 'View profile'),
            post ? `#/post/${id}` : `#/profile/${id}`,
            'text-button recommendation-view',
          ),
        );
      rail.append(item);
    }
    async function load(cursor) {
      if (pending || !active() || count >= 24) return;
      pending = true;
      section.setAttribute('aria-busy', 'true');
      tools.replaceChildren();
      const limit = Math.min(8, 24 - count);
      const params = new URLSearchParams({ context, format, contentScope, limit: String(limit) });
      if (postId) params.set('postId', postId);
      if (cursor) params.set('cursor', cursor);
      try {
        const result = await ctx.api(`/api/recommendations?${params}`);
        if (!active()) return;
        validateDiscoveryResponse(result, contentScope, discoveryPolicyVersion);
        const reasons = new Map((result.entries ?? []).map((entry) => [entry.id, entry.reason]));
        const pages = context === 'related' ? [] : (result.pages ?? []).slice(0, 4);
        // Cursor consumption has priority: every returned post must fit before optional pages.
        (result.posts ?? []).slice(0, limit).forEach((post) =>
          card({
            post,
            reason: reasons.get(post.id),
            snapshot: result.snapshot,
            exposureFormat: result.format,
          }),
        );
        pages.forEach((page) => card({ page, reason: page.reason }));
        if (!cursor && context === 'search' && result.topics?.length) {
          const topics = ctx.e('div', 'recommendation-topics');
          topics.setAttribute('aria-label', ctx.uiText('Explore topics'));
          result.topics.slice(0, 6).forEach((topic) =>
            topics.append(
              ctx.button(
                topic,
                () => {
                  if (!active()) return;
                  ctx.query = topic;
                  ctx.go('search');
                },
                'text-button',
              ),
            ),
          );
          section.insertBefore(topics, rail);
        }
        status.textContent = count
          ? ''
          : ctx.uiText('No suggestions right now. Try a topic or search.');
        ctx.observeReading?.();
        nextCursor = result.nextCursor === cursor ? null : result.nextCursor;
        if (nextCursor && count < 24)
          tools.append(ctx.button('More suggestions', () => load(nextCursor), 'text-button'));
      } catch (error) {
        if (!active()) return;
        status.textContent = ctx.uiText(error.message);
        tools.append(
          ctx.button(
            [400, 410].includes(error.status) ? 'Refresh suggestions' : 'Try again',
            () => {
              if ([400, 410].includes(error.status)) {
                seen.clear();
                count = 0;
                nextCursor = undefined;
                rail.replaceChildren();
                load();
              } else load(cursor);
            },
            'text-button',
          ),
        );
      } finally {
        pending = false;
        if (active()) section.removeAttribute('aria-busy');
      }
    }
    load();
    return section;
  }
  return { mount, dispose };
}
